import { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

import {
  getProfile,
  updateProfile,
  uploadImage,
  updateTheme,
} from "../api/user";
import { useTranslation } from "react-i18next";
import { faMoon } from "@fortawesome/free-regular-svg-icons";
import CustomPageHeader from "../components/pages/CustomPageHeader";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";

const ManageAccount = () => {
  const { t } = useTranslation();
  const [profileData, setProfileData] = useState({
    fullName: "",
    vibeHandle: "",
    email: "",
    gender: "",
  });

  // imageUrl is always the backend image path
  const [imageUrl, setImageUrl] = useState("");
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  // previewImage is for local preview only
  const [previewImage, setPreviewImage] = useState("");
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [originalImageUrl, setOriginalImageUrl] = useState(""); // Track backend image path
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState({ text: "", type: "" });
  const [showColorPicker, setShowColorPicker] = useState(false);
  // Initialize with dark mode color if dark mode is active, otherwise white
  const getInitialColor = () => {
    const isDark = document.documentElement.classList.contains('dark');
    return isDark ? "#0A0C1C" : "#ffffff";
  };
  const [selectedColor, setSelectedColor] = useState(getInitialColor);
  const [savedColor, setSavedColor] = useState("#ffffff");
  const colorWheelRef = useRef(null);
  const galleryInputRefs = useRef([null, null, null, null]);
  const [loader, setLoader] = useState(true);

  useEffect(() => {
    async function fetchProfile() {
      try {
        setLoader(true);
        const response = await getProfile();
        if (response.data.success && response.data) {
          setProfileData({
            fullName: response.data.data.full_name || "",
            vibeHandle: response.data.data.user_name || "",
            email: response.data.data.email || "",
            gender: response.data.data.gender || "",
          });
          const backendImage = response.data.data.image?.trim()
            ? response.data.data.image
            : "";
          setImageUrl(backendImage);
          setOriginalImageUrl(backendImage);
          setPreviewImage(backendImage || "/logo.png");
          // Set initial background color from profile
          const bgColor = response.data.data.background_color || "#ffffff";
          // Use grey color in dark mode, otherwise use saved color
          const isDark = document.documentElement.classList.contains('dark');
          const initialColor = isDark ? "#0A0C1C" : bgColor;
          setSelectedColor(initialColor);
          setSavedColor(bgColor);
        }
      } catch (error) {
        setProfileData({
          fullName: "Rocco Salvatoree",
          vibeHandle: "Rocco",
          email: "test@yopmail.com",
          gender: "male",
          address: "",
        });
        setImageUrl("");
        setOriginalImageUrl("");
        setPreviewImage("/default-image.jpg");
        // Use grey color in dark mode, otherwise use white
        const isDark = document.documentElement.classList.contains('dark');
        const initialColor = isDark ? "#0A0C1C" : "#ffffff";
        setSelectedColor(initialColor);
        setSavedColor("#ffffff");
      }
      setLoader(false);
    }
    fetchProfile();
  }, []);

  // Listen for theme changes and update background accordingly
  useEffect(() => {
    const handleThemeChange = () => {
      const isDark = document.documentElement.classList.contains('dark');
      if (isDark) {
        setSelectedColor("#0A0C1C");
      } else {
        setSelectedColor(savedColor);
      }
    };

    // Listen for class changes on documentElement
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          handleThemeChange();
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, [savedColor]);

  // Always update page background when selectedColor changes
  // useEffect(() => {
  //   document.body.style.background = selectedColor || "#ffffff";
  // }, [selectedColor]);

  useEffect(() => {
    if (showColorPicker && colorWheelRef.current) {
      const canvas = colorWheelRef.current;
      const ctx = canvas.getContext("2d");
      const radius = canvas.width / 2;
      for (let angle = 0; angle < 360; angle++) {
        const startAngle = ((angle - 1) * Math.PI) / 180;
        const endAngle = (angle * Math.PI) / 180;
        ctx.beginPath();
        ctx.moveTo(radius, radius);
        ctx.arc(radius, radius, radius, startAngle, endAngle);
        ctx.closePath();
        ctx.fillStyle = `hsl(${angle}, 100%, 50%)`; // Make sure ctx and angle are defined in this scope
        ctx.fill();
      }
      // Overlay white/black for brightness
      const grad = ctx.createRadialGradient(
        radius,
        radius,
        0,
        radius,
        radius,
        radius
      );
      grad.addColorStop(0, "rgba(255,255,255,1)");
      grad.addColorStop(0.7, "rgba(255,255,255,0)");
      grad.addColorStop(1, "rgba(0,0,0,0.7)");
      ctx.globalAlpha = 1;
      ctx.beginPath();
      ctx.arc(radius, radius, radius, 0, 2 * Math.PI);
      ctx.closePath();
      ctx.fillStyle = grad;
      ctx.fill();
    }
  }, [showColorPicker]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // const isValidBackendImageUrl = url => {
  //   // Example: backend images start with '/uploads/' or a known domain
  //   if (!url) return false;
  //   return url.startsWith('/uploads/') || url.startsWith('http');
  // }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveMessage({ text: "", type: "" });

    // Validation
    if (!profileData.fullName.trim()) {
      setSaveMessage({
        text: t("please_enter_full_name", "Please enter your full name"),
        type: "error",
      });
      setIsSaving(false);
      return;
    }
    if (!profileData.vibeHandle.trim()) {
      setSaveMessage({
        text: t("please_enter_user_name", "Please enter your user name"),
        type: "error",
      });
      setIsSaving(false);
      return;
    }
    if (
      !profileData.email.trim() ||
      !/^\S+@\S+\.\S+$/.test(profileData.email)
    ) {
      setSaveMessage({
        text: t(
          "please_enter_valid_email",
          "Please enter a valid email address"
        ),
        type: "error",
      });
      setIsSaving(false);
      return;
    }
    if (!profileData.gender) {
      setSaveMessage({
        text: t("please_select_gender", "Please select your gender"),
        type: "error",
      });
      setIsSaving(false);
      return;
    }

    try {
      let finalImageUrl = originalImageUrl; // Always start with backend image path
      let payload = {
        full_name: profileData.fullName,
        user_name: profileData.vibeHandle,
        email: profileData.email,
        gender: profileData.gender,
      };
      // If new image selected, upload and use new URL
      if (selectedImageFile) {
        // setSaveMessage({ text: 'Uploading image...', type: 'info' })
        const res = await uploadImage(selectedImageFile);
        if (res && res.data && Array.isArray(res.data) && res.data.length > 0) {
          finalImageUrl = res.data[0];
          payload.image = finalImageUrl;
          // toast.success(t("image_uploaded_successfully")) // Only add image if new image uploaded
        }
      }
      // Do not send image field if not changed
      const response = await updateProfile(payload);
      if (response.data.success) {
        // setSaveMessage({
        //   text: 'Profile updated successfully!',
        //   type: 'success'
        // })
        toast.success(t("profile_updated_successfully"));
        setSelectedImageFile(null);
        // Always use backend image path for display
        updateUser({ ...response?.data?.data });
        const backendImage = response.data.data?.image || finalImageUrl;
        setImageUrl(backendImage);
        setOriginalImageUrl(backendImage); // Update backend image path
        setPreviewImage(backendImage || "/logo.png");
        window.dispatchEvent(new Event("profileImageUpdated"));
      } else {
        setSaveMessage({
          text: response.data.message || "Failed to update profile",
          type: "error",
        });
      }
    } catch (error) {
      setSaveMessage({
        text:
          error?.response?.data?.message ||
          "An error occurred while updating profile",
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleWheelClick = (e) => {
    const canvas = colorWheelRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const ctx = canvas.getContext("2d");
    const pixel = ctx.getImageData(x, y, 1, 1).data;
    const rgb = `rgb(${pixel[0]},${pixel[1]},${pixel[2]})`; // Make sure pixel is defined in this scope and rgb is used
    // Convert RGB to HEX
    const hex =
      "#" +
      [pixel[0], pixel[1], pixel[2]]
        .map((v) => v.toString(16).padStart(2, "0"))
        .join("");
    setSelectedColor(hex);
    // document.body.style.background = hex;
  };

  const openColorPicker = () => {
    setSelectedColor(savedColor);
    setShowColorPicker(true);
  };

  const handleThemeChange = async () => {
    try {
      await updateTheme({ background_color: selectedColor });
      setSavedColor(selectedColor);
      // document.body.style.background = selectedColor;
      setShowColorPicker(false);
      // setSaveMessage({ text: 'Theme updated!', type: 'success' })
    } catch (error) {
      setSaveMessage({
        text: t("failed_to_update_theme", "Failed to update theme"),
        type: "error",
      });
    }
  };

  const handleCancelTheme = () => {
    setSelectedColor(savedColor);
    // document.body.style.background = savedColor;
    setShowColorPicker(false);
  };

  const location = useLocation();
  
  const handleBack = () => {
    // Check if we came from Settings page
    const fromSettings = location.state?.from === 'settings' || 
                         document.referrer.includes('/settings');
    
    if (fromSettings) {
      navigate('/settings');
    } else {
      navigate('/home');
    }
  };

  return (
    <>
      <CustomPageHeader
        pageContainerClass=""
        title={t("my_profile", "Vibelinks")}
        onBack={handleBack}
        // hideBack={true}
        // showPageHeader={false}
      >
        <div
          className="p-4 relative"
          style={{
            background: selectedColor,
            transition: "background 0.3s ",
          }}
        >
          {" "}
          {loader && (
            <div className="absolute inset-0 flex justify-center items-center z-50" style={{ backgroundColor: selectedColor }}>
              <div
                className="loader"
                style={{
                  width: 40,
                  height: 40,
                  border: "4px solid #ccc",
                  borderTop: "4px solid #7c3aed",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  margin: "0 auto",
                }}
              ></div>
            </div>
          )}
          {/* <PageHeader title={t("my_profile")} backTo="/settings" /> */}
          <form onSubmit={handleSubmit}>
            <div className="flex justify-center ">
              <div className="relative" style={{ background: selectedColor }}>
                {/* Camera Icon */}
                <button
                  type="button"
                  onClick={() => galleryInputRefs.current[0]?.click()}
                  className="absolute top-0 right-3 bg-black bg-opacity-50 p-2 rounded-full hover:bg-opacity-70 transition z-10 w-10 h-10"
                >
                  <FontAwesomeIcon icon={faCamera} className="text-white" />
                </button>
                <div
                  className="relative w-32 h-32 mx-auto rounded-full overflow-hidden border-4 border-white"
                  style={{ background: selectedColor }}
                >
                  {/* Profile Image */}
                  <img
                    src={
                      selectedImageFile ? previewImage : imageUrl || "/logo.png"
                    }
                    alt="Profile"
                    className="w-full h-full object-cover text-gray-700"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/logo192.png";
                    }}
                  />
                  {/* Hidden File Input */}
                  <input
                    type="file"
                    accept="image/*"
                    ref={(el) => (galleryInputRefs.current[0] = el)}
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setSelectedImageFile(file);
                        setPreviewImage(URL.createObjectURL(file)); // Preview only
                      } else {
                        setSelectedImageFile(null);
                        setPreviewImage(imageUrl || "/logo.png");
                      }
                    }}
                  />
                </div>
              </div>
            </div>
            {/* Full Name */}
            <div className="mb-4" style={{ background: selectedColor }}>
              <label htmlFor="fullName" className="block mb-1 text-gray-800 dark:text-dark-text">
                {t("full_name")}
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  placeholder={t("full_name")}
                  value={profileData.fullName}
                  onChange={handleInputChange}
                  className="w-full p-4 border-b-2 border-gray-400 dark:border-dark-border text-gray-800 dark:text-dark-text rounded-none border-l-0 border-r-0 border-t-0 focus:outline-none focus:border-b-2"
                  autoFocus
                  style={{ caretColor: "#e32f2f", background: selectedColor }}
                />
              </div>
            </div>
            {/* Vibe Handle */}
            <div className="mb-4" style={{ background: selectedColor }}>
              <label htmlFor="vibeHandle" className="block mb-1 text-gray-800 dark:text-dark-text">
                {t("vibe_handle")}
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="vibeHandle"
                  name="vibeHandle"
                  placeholder={t("enter_vibe_handler")}
                  value={profileData.vibeHandle}
                  onChange={handleInputChange}
                  className="w-full p-4 border-b-2 border-gray-400 dark:border-dark-border text-gray-800 dark:text-dark-text rounded-none border-l-0 border-r-0 border-t-0 focus:outline-none focus:border-b-2"
                  style={{ caretColor: "#e32f2f", background: selectedColor }}
                />
              </div>
            </div>
            {/* Email */}
            <div className="mb-4" style={{ background: selectedColor }}>
              <label htmlFor="email" className="block mb-1 text-gray-800 dark:text-dark-text">
                {t("email")}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder={t("email")}
                value={profileData.email}
                onChange={handleInputChange}
                className="w-full p-4 border-b-2 border-gray-400 dark:border-dark-border text-gray-800 dark:text-dark-text rounded-none border-l-0 border-r-0 border-t-0 focus:outline-none focus:border-b-2"
                style={{ background: selectedColor }}
                disabled
              />
            </div>
            {/* Gender */}
            <div className="mb-4" style={{ background: selectedColor }}>
              <label htmlFor="gender" className="block mb-1 text-gray-800 dark:text-dark-text">
                {t("gender")}
              </label>
              <select
                id="gender"
                name="gender"
                placeholder={t("gender")}
                value={profileData.gender || ""}
                onChange={handleInputChange}
                className="w-full p-4 border-b-2 border-gray-400 dark:border-dark-border text-gray-800 dark:text-dark-text rounded-none border-l-0 border-r-0 border-t-0 focus:outline-none focus:border-b-2"
                style={{ background: selectedColor }}
              >
                <option value="" className="dark:bg-[#252739] dark:text-white">{t("select", "Select")}</option>
                <option value="Male" className="dark:bg-[#252739] dark:text-white">{t("male")}</option>
                <option value="Female" className="dark:bg-[#252739] dark:text-white">{t("female")}</option>
                <option value="Others" className="dark:bg-[#252739] dark:text-white">{t("other")}</option>
              </select>
            </div>
            {/* Feedback message */}
            {saveMessage.text && (
              <div
                className={`p-4 rounded-lg mt-4 ${
                  saveMessage.type === "success"
                    ? "bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300"
                    : "bg-red-100 dark:bg-red-900 text-red-500 dark:text-red-300"
                }`}
              >
                {saveMessage.text}
              </div>
            )}
            {/* Save Button */}
            <button
              type="submit"
              disabled={isSaving}
              className="w-full bg-[#6d29f6] text-white font-bold py-4 px-4 rounded-xl mt-8 hover:bg-purple-600 transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 disabled:opacity-70"
            >
              {isSaving
                ? t("saving", "Saving...")
                : t("save_profile", "Save Profile")}
            </button>
            <button
              type="button"
              className="w-full bg-[#6D29F6] text-white font-bold py-4 px-4 rounded-xl mt-4  transition-colors focus:outline-none focus:ring-2 focus:ring-[#6d29f6] focus:ring-opacity-50"
              onClick={() => {
                navigate("/subscription");
              }}
            >
              {t("subscription", "")}
            </button>
          
            {/* Color Wheel Picker Modal */}
            {showColorPicker && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  background: "rgba(0,0,0,0.4)",
                  zIndex: 9999,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  className="flex flex-col items-center gap-2 p-6 bg-white dark:bg-[#252739] rounded-xl shadow-lg"
                  style={{ minWidth: 320 }}
                >
                  {/* <div className='mb-2 font-bold'>
                    Current:{' '}
                    <span style={{ color: savedColor }}>{savedColor}</span>
                  </div> */}
                  <canvas
                    ref={colorWheelRef}
                    width={220}
                    height={220}
                    style={{
                      borderRadius: "50%",
                      cursor: "crosshair",
                      marginBottom: 16,
                    }}
                    onClick={handleWheelClick}
                  />
                  {/* <div className='mb-2 font-bold'>
                    Selected:{' '}
                    <span style={{ color: selectedColor }}>
                      {selectedColor}
                    </span>
                  </div> */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="px-4 py-2 bg-gray-300 text-[#6d29f6] rounded-xl font-bold"
                      onClick={handleCancelTheme}
                    >
                      {t("cancel", "Cancel")}
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 bg-[#6d29f6] text-white rounded-xl font-bold"
                      onClick={handleThemeChange}
                    >
                      {t("change", "Change")}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      </CustomPageHeader>
    </>
  );
};

export default ManageAccount;
